import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PageableRequest } from 'src/app/admin-api';
import { PageContent } from 'src/app/components/table';
import { StorageService } from 'src/app/services/auth/storage.service';
import { environment } from 'src/environments/environment';

interface ResponseB4A<T> {
  result: T;
}

export interface BillingCycleWorkloadStatus {
  kind?: string;
  name?: string;
  running?: boolean | null;
  suspended?: boolean | null;
  replicas?: number | null;
  runningPods?: number | null;
  statusLabel?: string | null;
}

export interface BillingCycleStageCard {
  stage?: string;
  label?: string;
  queueCount?: number;
  queueSource?: string;
  producer?: BillingCycleWorkloadStatus | null;
  consumer?: BillingCycleWorkloadStatus | null;
}

export interface BillingCycleOverview {
  stages?: BillingCycleStageCard[];
}

export interface BillingCycleSubscriberPaymentStep {
  subscriberId?: number;
  paymentRoundId?: number;
  step?: number | string;
  dateCreated?: string;
  dateUpdated?: string;
  lastMessage?: string;
  creditCardPaymentId?: number;
  origin?: string;
  priority?: number;
  gatewayPriorityId?: number;
  attempt?: number;
  cardPriority?: number;
  annualMonthly?: number;
}

export interface BillingCycleAttemptSummary {
  attempt?: number;
  totalRows?: number;
  inFlight?: number;
  failed?: number;
  paidPath?: number;
  distinctSubscribers?: number;
  neverChargedCount?: number;
  approvalRatePercent?: number | null;
  marginVsAttempt1Pp?: number | null;
}

export interface BillingCycleUnchargedCounts {
  noCard?: number;
  noToken?: number;
  other?: number;
  total?: number;
}

export interface BillingCycleMonthSummary {
  yyyymm?: string;
  eligibleUniverse?: number;
  paidPath?: number;
  inFlight?: number;
  failed?: number;
  remainingToEnqueue?: number;
  suggestedAttempt?: number;
  byAttempt?: BillingCycleAttemptSummary[];
  unchargedCounts?: BillingCycleUnchargedCounts;
}

export interface BillingCycleErrorRankingItem {
  kind?: string;
  lastMessage?: string;
  paymentResponseMessageType?: string;
  lr?: string;
  step?: number;
  subscriberCount?: number;
  percentOfFailed?: number | null;
}

export interface BillingCycleUnchargedRow {
  subscriberId?: number;
  email?: string;
  subscriberStatus?: number;
  attempt?: number;
  paymentRoundId?: number;
  step?: number;
  lastMessage?: string;
  cohort?: string;
  hasCard?: boolean;
  hasAnyToken?: boolean;
  tokenGatewayIds?: string;
}

export interface BillingCycleErrorSubscriberRow {
  subscriberId?: number;
  email?: string;
  paymentRoundId?: number;
  attempt?: number;
  step?: number;
  lastMessage?: string;
  tid?: string;
  kind?: string;
}

export interface BillingCycleLifecycleFilters {
  yyyymm?: string | null;
  attempt?: number | null;
  paymentRoundId?: number | null;
  annualMonthly?: number | null;
  cohort?: string | null;
  kind?: string | null;
  messageType?: string | null;
  lr?: string | null;
  lastMessage?: string | null;
}

export interface BillingCycleLoteItem {
  paymentRoundId?: number;
  dateCreated?: string;
  dateScheduled?: string;
  dateExecuted?: string;
  subscriberCount?: number;
  username?: string;
  maxAttempt?: number;
  distinctSubscribers?: number;
  /** SubscriberPaymentStepEnum.VALIDATION (0) */
  validationCount?: number;
  /** SubscriberPaymentStepEnum.PAYMENT (1) */
  paymentCount?: number;
  /** SubscriberPaymentStepEnum.FAILED (2) */
  failedCount?: number;
  /** SubscriberPaymentStepEnum.PAID (3) */
  paidCount?: number;
  /** SubscriberPaymentStepEnum.ACTIVATION (4) */
  activationCount?: number;
  /** SubscriberPaymentStepEnum.SUBSCRIBER_EDITION (5) */
  subscriberEditionCount?: number;
  /** SubscriberPaymentStepEnum.FINISHED (6) */
  finishedCount?: number;
}

export interface BillingCycleEnqueueLoteRequest {
  attempt?: number;
  batchSize?: number;
  onlyMonthly?: boolean;
  maxBatches?: number;
}

export interface BillingCycleLoteBatchResult {
  paymentRoundId?: number;
  enqueuedCount?: number;
  remainingEligibleCount?: number;
}

export interface BillingCycleEnqueueLoteResult {
  attempt?: number;
  batchSize?: number;
  onlyMonthly?: boolean;
  batchesExecuted?: number;
  totalEnqueued?: number;
  lastPaymentRoundId?: number;
  remainingEligibleCount?: number;
  batches?: BillingCycleLoteBatchResult[];
}

export interface BillingCycleGatewayReportRow {
  gatewayId?: number | null;
  gatewayName?: string | null;
  distinctSubscribers?: number | null;
  paid?: number | null;
  refused?: number | null;
  inFlight?: number | null;
  approvalRatePercent?: number | null;
  sharePercent?: number | null;
}

export interface BillingCycleWithoutGatewayAttempt {
  label?: string | null;
  distinctSubscribers?: number | null;
  paid?: number | null;
  refused?: number | null;
  inFlight?: number | null;
  approvalRatePercent?: number | null;
  sharePercent?: number | null;
}

export interface BillingCycleGatewayReport {
  yyyymm?: string | null;
  attempt?: number | null;
  annualMonthly?: boolean | null;
  gateways?: BillingCycleGatewayReportRow[] | null;
  withoutGatewayAttempt?: BillingCycleWithoutGatewayAttempt | null;
}

@Injectable({ providedIn: 'root' })
export class BillingCycleService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/billing-cycle/admin`;

  getOverview(): Observable<BillingCycleOverview> {
    return this.http
      .get<ResponseB4A<BillingCycleOverview>>(`${this.baseUrl}/overview`, {
        headers: this.authHeaders()
      })
      .pipe(map((response) => response.result ?? { stages: [] }));
  }

  getMonthSummary(
    attempt?: number | null,
    yyyymm?: string | null,
    annualMonthly?: number | null
  ): Observable<BillingCycleMonthSummary> {
    return this.http
      .get<ResponseB4A<BillingCycleMonthSummary>>(`${this.baseUrl}/month-summary`, {
        headers: this.authHeaders(),
        params: this.lifecycleParams({ yyyymm, attempt, annualMonthly })
      })
      .pipe(map((response) => response.result ?? {}));
  }

  getUnchargedCounts(
    filters: BillingCycleLifecycleFilters
  ): Observable<BillingCycleUnchargedCounts> {
    return this.http
      .get<ResponseB4A<BillingCycleUnchargedCounts>>(
        `${this.baseUrl}/lifecycle/uncharged-counts`,
        { headers: this.authHeaders(), params: this.lifecycleParams(filters) }
      )
      .pipe(map((response) => response.result ?? {}));
  }

  listErrorRanking(
    filters: BillingCycleLifecycleFilters
  ): Observable<BillingCycleErrorRankingItem[]> {
    return this.http
      .get<ResponseB4A<BillingCycleErrorRankingItem[]>>(
        `${this.baseUrl}/lifecycle/errors`,
        { headers: this.authHeaders(), params: this.lifecycleParams(filters) }
      )
      .pipe(map((response) => response.result ?? []));
  }

  listUncharged(
    request: PageableRequest,
    filters: BillingCycleLifecycleFilters
  ): Observable<PageContent<BillingCycleUnchargedRow>> {
    return this.http
      .put<ResponseB4A<PageContent<BillingCycleUnchargedRow>>>(
        `${this.baseUrl}/lifecycle/uncharged`,
        request,
        { headers: this.authHeaders(), params: this.lifecycleParams(filters) }
      )
      .pipe(
        map((response) => response.result as PageContent<BillingCycleUnchargedRow>)
      );
  }

  listErrorSubscribers(
    request: PageableRequest,
    filters: BillingCycleLifecycleFilters
  ): Observable<PageContent<BillingCycleErrorSubscriberRow>> {
    return this.http
      .put<ResponseB4A<PageContent<BillingCycleErrorSubscriberRow>>>(
        `${this.baseUrl}/lifecycle/error-subscribers`,
        request,
        { headers: this.authHeaders(), params: this.lifecycleParams(filters) }
      )
      .pipe(
        map(
          (response) =>
            response.result as PageContent<BillingCycleErrorSubscriberRow>
        )
      );
  }

  exportUnchargedCsv(filters: BillingCycleLifecycleFilters): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/lifecycle/uncharged.csv`, {
      headers: this.authHeaders(),
      params: this.lifecycleParams(filters),
      responseType: 'blob'
    });
  }

  listLotes(yyyymm?: string | null): Observable<BillingCycleLoteItem[]> {
    let params = new HttpParams();
    if (yyyymm) {
      params = params.set('yyyymm', yyyymm);
    }
    return this.http
      .get<ResponseB4A<BillingCycleLoteItem[]>>(`${this.baseUrl}/lotes`, {
        headers: this.authHeaders(),
        params
      })
      .pipe(map((response) => response.result ?? []));
  }

  getGatewayReport(
    yyyymm: string,
    attempt?: number | null,
    annualMonthly?: boolean | null
  ): Observable<BillingCycleGatewayReport> {
    let params = new HttpParams().set('yyyymm', yyyymm);
    if (attempt != null && Number.isFinite(attempt) && attempt >= 1) {
      params = params.set('attempt', Math.trunc(attempt));
    }
    if (annualMonthly != null) {
      params = params.set('annualMonthly', annualMonthly);
    }
    return this.http
      .get<ResponseB4A<BillingCycleGatewayReport>>(
        `${this.baseUrl}/report/gateways`,
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(
        map(
          (response) =>
            response.result ?? {
              yyyymm,
              gateways: []
            }
        )
      );
  }

  enqueueLote(
    request: BillingCycleEnqueueLoteRequest
  ): Observable<BillingCycleEnqueueLoteResult> {
    return this.http
      .post<ResponseB4A<BillingCycleEnqueueLoteResult>>(
        `${this.baseUrl}/enqueue-lote`,
        request,
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result ?? {}));
  }

  controlProducer(stage: string, action: 'play' | 'pause' | 'stop'): Observable<string> {
    return this.http
      .post<ResponseB4A<string>>(
        `${this.baseUrl}/stages/${encodeURIComponent(stage)}/producer/${action}`,
        {},
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  controlConsumer(stage: string, action: 'play' | 'stop'): Observable<string> {
    return this.http
      .post<ResponseB4A<string>>(
        `${this.baseUrl}/stages/${encodeURIComponent(stage)}/consumer/${action}`,
        {},
        { headers: this.authHeaders() }
      )
      .pipe(map((response) => response.result));
  }

  listSubscriberPaymentSteps(
    request: PageableRequest,
    step?: number | null,
    paymentRoundId?: number | null,
    attempt?: number | null
  ): Observable<PageContent<BillingCycleSubscriberPaymentStep>> {
    let params = new HttpParams();
    if (step != null) {
      params = params.set('step', step);
    }
    if (paymentRoundId != null) {
      params = params.set('paymentRoundId', paymentRoundId);
    }
    if (attempt != null) {
      params = params.set('attempt', attempt);
    }
    return this.http
      .put<ResponseB4A<PageContent<BillingCycleSubscriberPaymentStep>>>(
        `${this.baseUrl}/subscriber-payment-steps`,
        request,
        {
          headers: this.authHeaders(),
          params
        }
      )
      .pipe(
        map(
          (response) =>
            response.result as PageContent<BillingCycleSubscriberPaymentStep>
        )
      );
  }

  private lifecycleParams(filters: BillingCycleLifecycleFilters): HttpParams {
    let params = new HttpParams();
    if (filters.yyyymm) {
      params = params.set('yyyymm', filters.yyyymm);
    }
    if (filters.attempt != null) {
      params = params.set('attempt', filters.attempt);
    }
    if (filters.paymentRoundId != null) {
      params = params.set('paymentRoundId', filters.paymentRoundId);
    }
    if (filters.annualMonthly != null) {
      params = params.set('annualMonthly', filters.annualMonthly);
    }
    if (filters.cohort) {
      params = params.set('cohort', filters.cohort);
    }
    if (filters.kind) {
      params = params.set('kind', filters.kind);
    }
    if (filters.messageType) {
      params = params.set('messageType', filters.messageType);
    }
    if (filters.lr != null && filters.lr !== '') {
      params = params.set('lr', filters.lr);
    }
    if (filters.lastMessage) {
      params = params.set('lastMessage', filters.lastMessage);
    }
    return params;
  }

  private authHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    if (StorageService.userLogged && StorageService.token) {
      return headers.set('Authorization', `Bearer ${StorageService.token}`);
    }
    return headers;
  }
}
